/**
 * Paper used per roll during the latest print run of a plotter order.
 * order.paperLevels is written by IMG-Plotter in raw printer lingerLength units.
 */

import { TranslateService } from '@ngx-translate/core';

const LINGER_UNITS_PER_METER = 100000;

export type PlotterPaperUsageState = 'used' | 'in-progress' | 'unreliable' | 'unknown';

export interface PlotterPaperUsageRow {
  paperSerialName: string | null;
  state: PlotterPaperUsageState;
  meters?: number;
}

export function hasPlotterPaperUsage(order: any): boolean {
  return Array.isArray(order?.paperLevels) && order.paperLevels.length > 0;
}

/** paperLevels holds every roll on the printer - keep only the rolls the order's images were set to. */
export function getPlotterPaperUsageRows(order: any): PlotterPaperUsageRow[] {
  const levels: any[] = hasPlotterPaperUsage(order) ? order.paperLevels : [];
  const orderPaperCodes = new Set<string>();
  for (const file of order?.files || []) {
    for (const image of file?.images || []) {
      if (image?.printSettings?.paperType) {
        orderPaperCodes.add(image.printSettings.paperType);
      }
    }
  }
  const relevant = levels.filter(level => orderPaperCodes.has(level?.paperType?.paperPrinterCode));

  return (relevant.length ? relevant : levels).map(level => {
    const paperSerialName = level?.paperType?.paperType || null;
    const start = level?.atFirstInQueue;
    const end = level?.atLeftQueue;
    if (typeof start !== 'number') {
      return { paperSerialName, state: 'unknown' };
    }
    if (typeof end !== 'number') {
      return { paperSerialName, state: 'in-progress' };
    }
    if (end > start) {
      return { paperSerialName, state: 'unreliable' };
    }
    const meters = Math.round(((start - end) / LINGER_UNITS_PER_METER) * 100) / 100;
    return { paperSerialName, state: 'used', meters };
  });
}

/** Multi-line text for a tooltip with matTooltipClass="monitor-paper-tooltip" (white-space: pre-line). */
export function buildPlotterPaperUsageTooltip(order: any, translate: TranslateService): string {
  const metersUnit = translate.instant('su-management.branches_m');
  const lines: string[] = [translate.instant('paper-usage.title')];

  for (const row of getPlotterPaperUsageRows(order)) {
    const paperName = row.paperSerialName
      ? translate.instant('paper-type.name-short.' + row.paperSerialName)
      : '—';
    let value = '—';
    if (row.state === 'used') {
      value = `${row.meters} ${metersUnit}`;
    } else if (row.state === 'in-progress') {
      value = translate.instant('paper-usage.in-progress');
    } else if (row.state === 'unreliable') {
      value = translate.instant('paper-usage.unreliable');
    }
    lines.push(`${paperName}: ${value}`);
  }

  if (typeof order?.totalOrderLengthInMeters === 'number') {
    const calculated = Math.round(order.totalOrderLengthInMeters * 100) / 100;
    lines.push(`${translate.instant('paper-usage.calculated')}: ${calculated} ${metersUnit}`);
  }

  return lines.join('\n');
}
